import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Workflow, WorkflowComponent} from '../types/api';
import {workflowHasComponentErrors} from '../utils/workflowComponentErrors';

// Draft node structure for UI state before backend persistence
export type DraftNode = {
  uiId: string;
  type: NodeType;
  name?: string;
  storeAs?: string;
  ruleExpression?: string;
  inputSchema?: Record<string, unknown>;
  constants?: Record<string, unknown>;
  parentId?: string; // captured on drop
  /**
   * When a draft node is inserted between two existing components (A -> C -> B),
   * this stores B's backend component id for `/insert-between` APIs.
   */
  afterChildComponentId?: string;
  /** Explicit marker for "inserted in middle" drafts. */
  isInsertBetween?: boolean;
  /**
   * When a Ruleset is inserted mid-edge, which branch receives the existing child.
   * `bottom-right` = pass/true, `bottom-left` = reject/false.
   */
  rulesetInsertBranch?: 'bottom-right' | 'bottom-left';
  backendId?: string; // set after first submit
  dirty?: boolean;
  // Additional type-specific properties
  value?: string; // for decision nodes
  message?: string; // for decision nodes
  outputs?: string[]; // for decision nodes
  inputReferenceKey?: string; // for input nodes
};

import {Edge} from 'reactflow';
import {NodeType} from '@core/interfaces/customNode';

export interface WorkflowState {
  // Core workflow data
  workflowId: string;
  workflow: Workflow | null;
  inputComponentId?: string; // from GET workflow
  inputProperties?: Record<string, Record<string, unknown>>; // inputSchema properties for validation
  inputConstants?: Record<string, {type: string; name: string; value: unknown}>; // constants from input component

  // Server components cache
  serverById: Record<string, WorkflowComponent>;

  // Draft state (UI modifications before persistence)
  draftByUiId: Record<string, DraftNode>;

  // Edges (for parent/connection tracking)
  edges: Edge[];

  // Loading states
  isLoading: boolean;
  loadingOperations: string[]; // Track specific operations

  // Error handling
  error: string | null;
  hasComponentErrors: boolean; // Flag to track if any components have errors
}

const initialState: WorkflowState = {
  workflowId: '',
  workflow: null,
  inputComponentId: undefined,
  inputProperties: undefined,
  inputConstants: undefined,
  serverById: {},
  draftByUiId: {},
  isLoading: false,
  loadingOperations: [],
  error: null,
  edges: [],
  hasComponentErrors: false,
};

const workflowSlice = createSlice({
  name: 'ruleDetailWorkflow',
  initialState,
  reducers: {
    // Edge management
    addEdge: (state, action: PayloadAction<Edge>) => {
      // Only allow one incoming edge per target
      state.edges = [
        ...state.edges.filter((e) => e.target !== action.payload.target),
        action.payload,
      ];
    },
    setEdges: (state, action: PayloadAction<Edge[]>) => {
      state.edges = action.payload;
    },
    setNodeParent: (
      state,
      action: PayloadAction<{uiId: string; parentId?: string}>
    ) => {
      const draft = state.draftByUiId[action.payload.uiId];
      if (draft) {
        draft.parentId = action.payload.parentId;
        draft.dirty = true;
      }
    },

    // Core workflow setters
    setWorkflow: (state, action: PayloadAction<Workflow>) => {
      state.workflow = action.payload;
      state.hasComponentErrors = workflowHasComponentErrors(action.payload);
    },
    setWorkflowId: (state, action: PayloadAction<string>) => {
      state.workflowId = action.payload;
    },
    setInputComponentId: (state, action: PayloadAction<string>) => {
      state.inputComponentId = action.payload;
    },
    setInputProperties: (
      state: WorkflowState,
      action: PayloadAction<Record<string, Record<string, unknown>>>
    ) => {
      state.inputProperties = action.payload;
    },
    setInputConstants: (
      state: WorkflowState,
      action: PayloadAction<
        Record<string, {type: string; name: string; value: unknown}>
      >
    ) => {
      state.inputConstants = action.payload;
    },

    // Server components management
    setServerComponent: (
      state,
      action: PayloadAction<{id: string; component: WorkflowComponent}>
    ) => {
      state.serverById[action.payload.id] = action.payload.component;
    },

    updateServerComponents: (
      state,
      action: PayloadAction<Record<string, WorkflowComponent>>
    ) => {
      state.serverById = {...state.serverById, ...action.payload};
    },

    // Draft management
    captureParentId: (
      state,
      action: PayloadAction<{targetUiId: string; parentId: string}>
    ) => {
      const existingDraft = state.draftByUiId[action.payload.targetUiId];
      state.draftByUiId[action.payload.targetUiId] = {
        ...existingDraft,
        parentId: action.payload.parentId,
        dirty: true,
      };
    },

    updateDraft: (
      state,
      action: PayloadAction<{uiId: string; patch: Partial<DraftNode>}>
    ) => {
      const existingDraft = state.draftByUiId[action.payload.uiId];
      state.draftByUiId[action.payload.uiId] = {
        ...existingDraft,
        ...action.payload.patch,
        dirty: true,
      };
    },

    createDraftNode: (state, action: PayloadAction<DraftNode>) => {
      state.draftByUiId[action.payload.uiId] = {...action.payload, dirty: true};
    },

    removeDraftNode: (state, action: PayloadAction<string>) => {
      delete state.draftByUiId[action.payload];
    },

    markBackendId: (
      state,
      action: PayloadAction<{uiId: string; backendId: string}>
    ) => {
      if (state.draftByUiId[action.payload.uiId]) {
        state.draftByUiId[action.payload.uiId].backendId =
          action.payload.backendId;
        state.draftByUiId[action.payload.uiId].dirty = false;
      }
    },

    clearDirty: (state, action: PayloadAction<string>) => {
      if (state.draftByUiId[action.payload]) {
        state.draftByUiId[action.payload].dirty = false;
      }
    },

    // Loading state management
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setOperationLoading: (
      state,
      action: PayloadAction<{operation: string; isLoading: boolean}>
    ) => {
      if (action.payload.isLoading) {
        if (!state.loadingOperations.includes(action.payload.operation)) {
          state.loadingOperations.push(action.payload.operation);
        }
      } else {
        state.loadingOperations = state.loadingOperations.filter(
          (op) => op !== action.payload.operation
        );
      }
      state.isLoading = state.loadingOperations.length > 0;
    },

    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setComponentErrorFlag: (state, action: PayloadAction<boolean>) => {
      state.hasComponentErrors = action.payload;
    },

    // Reset state
    reset: () => {
      return initialState;
    },
  },
});

export const workflowActions = workflowSlice.actions;
export default workflowSlice.reducer;

// Utility functions to use in components
export const workflowUtils = {
  getDraftNode: (state: WorkflowState, uiId: string): DraftNode | undefined => {
    return state.draftByUiId[uiId];
  },
  getServerComponent: (
    state: WorkflowState,
    id: string
  ): WorkflowComponent | undefined => {
    return state.serverById[id];
  },
  isDraftDirty: (state: WorkflowState, uiId: string): boolean => {
    return state.draftByUiId[uiId]?.dirty || false;
  },
  getAllDraftNodes: (state: WorkflowState): DraftNode[] => {
    return Object.values(state.draftByUiId);
  },
  getParentFromEdges: (
    state: WorkflowState,
    nodeId: string
  ): string | undefined => {
    const incoming = state.edges.find((e) => e.target === nodeId);
    return incoming?.source;
  },
};
